(ns app.front.ui.tiptap
  "Tiptap rich editor. Uses a limited selection of extensions to support the required text effects. Any unsupported HTML elements
  will be stripped from the content. Produces HTML content with inline styles."
  ;; Tiptap 3 exports some extensions only as named exports, so :refer is unavoidable here.
  {:clj-kondo/config '{:linters {:refer {:level :off}}}}
  (:require ["@carbon/icons-react/es/text--bold/24" :default TextBold24]
            ["@carbon/icons-react/es/text--indent--less/24" :default TextIndentLess24]
            ["@carbon/icons-react/es/text--indent--more/24" :default TextIndentMore24]
            ["@carbon/icons-react/es/text--italic/24" :default TextItalic24]
            ["@carbon/icons-react/es/text--superscript/24" :default TextSuperscript24]
            ["@carbon/icons-react/es/text--underline/24" :default TextUnderline24]
            ["@tiptap/core" :as tiptap]
            ["@tiptap/extension-blockquote" :default Blockquote]
            ["@tiptap/extension-bold" :default Bold]
            ["@tiptap/extension-document" :default Document]
            ["@tiptap/extension-heading" :default Heading]
            ["@tiptap/extension-italic" :default Italic]
            ["@tiptap/extension-link" :default Link]
            ["@tiptap/extension-list" :refer (BulletList ListItem OrderedList)]
            ["@tiptap/extension-paragraph" :default Paragraph]
            ["@tiptap/extension-superscript" :default Superscript]
            ["@tiptap/extension-text" :default Text]
            ["@tiptap/extension-text-style" :refer (TextStyle)]
            ["@tiptap/extension-underline" :default Underline]
            ["@tiptap/extensions" :refer (Placeholder UndoRedo)]
            ["@tiptap/pm/state" :as pm-state]
            [app.front.ui :as ui]
            [app.front.ui.phrase-code-modal :as phrase-code-modal]
            [app.front.ui.select :as ui-select]
            [app.front.view.group.subs :as group-subs]
            [app.templates.model :as templates-model]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.util.util :as util]
            [goog.dom :as dom]
            [goog.events :as googe]
            [goog.Timer :as timer]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

;; Extend the Paragraph extension to support a fontSize attribute
(def CustomParagraph
  (.extend Paragraph
           (clj->js {:addAttributes
                     (fn []
                       (clj->js {:fontSize
                                 {:default    nil
                                  :parseHTML  (fn [element]
                                                (.-fontSize (.. element -style)))
                                  :renderHTML (fn [attributes]
                                                #js {:style (when (-> (.-fontSize attributes) util/nil-or-blank? not)
                                                              (str "font-size: " (.-fontSize attributes)))})}}))})))

(def NoSpacing
  (.create tiptap/Node
           (clj->js {:name       "noSpacing"
                     :group      "block"
                     :content    "inline*"
                     :parseHTML  (fn []
                                   (clj->js [{:tag "span[data-type=\"no-spacing\"]"}
                                             {:tag      "span"
                                              :getAttrs (fn [element]
                                                          (let [parent (.-parentElement element)]
                                                            (if (and parent
                                                                     (= "DIV" (.-tagName parent))
                                                                     (some-> (.-nextElementSibling element)
                                                                             .-tagName
                                                                             (= "BR")))
                                                              #js {}
                                                              false)))}]))
                     :renderHTML (fn []
                                   (clj->js ["div" {}
                                             ["span" {"data-type" "no-spacing"} 0]
                                             ["br"]]))})))

(defn- text-containing-placeholder-delimiter? [^js fragment]
  (let [found? (atom false)]
    (.descendants fragment
                  (fn [^js node]
                    (if (and (.-isText node)
                             (str/includes? (.-text node) "%"))
                      (do
                        (reset! found? true)
                        false)
                      (not @found?))))
    @found?))

(defn- transaction-inserts-placeholder-delimiter? [^js transaction]
  (when (.-docChanged transaction)
    (some (fn [^js step]
            (some-> step
                    .-slice
                    .-content
                    text-containing-placeholder-delimiter?))
          (array-seq (.-steps transaction)))))

(defn- should-normalize-template-placeholders? [transactions]
  (some transaction-inserts-placeholder-delimiter? (array-seq transactions)))

(defn- collect-template-placeholder-replacements [^js doc ^js template-placeholder-node placeholder-ids]
  (let [replacements (atom [])]
    (.descendants doc
                  (fn [^js node pos]
                    (when (.-isText node)
                      (let [text (.-text node)]
                        (loop [matches (re-seq templates-model/percent-placeholder-pattern text)
                               offset  0]
                          (when-let [match (first matches)]
                            (let [token (first match)
                                  id    (second match)
                                  idx   (.indexOf text token offset)]
                              (when (contains? placeholder-ids id)
                                (swap! replacements conj {:from (+ pos idx)
                                                          :to   (+ pos idx (count token))
                                                          :node (.create template-placeholder-node
                                                                         (clj->js {:placeholderId id})
                                                                         nil
                                                                         (.-marks node))}))
                              (recur (rest matches) (+ idx (count token))))))))
                    true))
    @replacements))

(defn- normalize-template-placeholder-doc [tr doc template-placeholder-node placeholder-ids]
  (when (seq placeholder-ids)
    (let [replacements (collect-template-placeholder-replacements doc template-placeholder-node placeholder-ids)]
      (when (seq replacements)
        (doseq [{:keys [from to node]} (sort-by :from > replacements)]
          (.replaceWith tr from to node))
        (.setMeta ^js tr "addToHistory" false)
        tr))))

(defn- normalize-template-placeholder-editor! [^js editor placeholder-ids]
  (let [state (.-state editor)]
    (when-let [tr (normalize-template-placeholder-doc
                    (.-tr state)
                    (.-doc state)
                    (.. state -schema -nodes -templatePlaceholder)
                    placeholder-ids)]
      (.dispatch (.-view editor) tr))))

(def TemplatePlaceholder
  "Tiptap extension for rendering a special pill around %placeholder% when the magic word is something defined as
   an allowed placeholder. To use, define `template-placeholders` on the editor component. The data- tags will get
   removed on html sanitization."
  (.create tiptap/Node
           (clj->js {:name          "templatePlaceholder"
                     :group         "inline"
                     :inline        true
                     :atom          true
                     :selectable    true
                     :addOptions    (fn []
                                      (clj->js {:placeholderIds #{}}))
                     :addAttributes (fn []
                                      (clj->js {:placeholderId
                                                {:default    nil
                                                 :parseHTML  (fn [element]
                                                               (.getAttribute element "data-id"))
                                                 :renderHTML (fn [attributes]
                                                               #js {"data-id" (aget attributes "placeholderId")})}}))
                     :parseHTML     (fn []
                                      (clj->js [{:tag "span[data-type=\"template-placeholder\"]"}]))
                     :renderHTML    (fn [props]
                                      (let [attrs (aget props "HTMLAttributes")
                                            id    (or (aget attrs "placeholderId")
                                                      (aget attrs "data-id"))]
                                        (clj->js ["span" {"data-type"       "template-placeholder"
                                                          "data-id"         id
                                                          "class"           "template-placeholder"
                                                          "contenteditable" "false"}
                                                  (str "%" id "%")])))
                     :addProseMirrorPlugins
                     (fn []
                       (this-as this
                                (let [options         (.-options ^js this)
                                      placeholder-ids (set (js->clj (.-placeholderIds ^js options)))]
                                  (clj->js
                                    ;; this plugin is needed to reliably catch edits to the text for addition of the
                                    ;; placeholder tags
                                    [(pm-state/Plugin.
                                       #js {:key               (pm-state/PluginKey. "templatePlaceholder")
                                            :appendTransaction (fn [transactions _ ^js new-state]
                                                                 (when (should-normalize-template-placeholders?
                                                                         transactions)
                                                                   (normalize-template-placeholder-doc
                                                                     (.-tr new-state)
                                                                     (.-doc new-state)
                                                                     (.. new-state -schema -nodes -templatePlaceholder)
                                                                     placeholder-ids)))})]))))})))

(defn- placeholder-span [doc id]
  (let [el (.createElement doc "span")]
    (.setAttribute el "data-type" "template-placeholder")
    (.setAttribute el "data-id" id)
    (.setAttribute el "class" "template-placeholder")
    (.setAttribute el "contenteditable" "false")
    (set! (.-textContent el) (str "%" id "%"))
    el))

(defn- replace-placeholder-text-node! [doc placeholder-ids node]
  (let [text    (.-nodeValue node)
        matches (re-seq templates-model/percent-placeholder-pattern text)]
    (when (seq matches)
      (let [fragment (.createDocumentFragment doc)]
        (loop [remaining matches
               offset    0]
          (if-let [match (first remaining)]
            (let [token (first match)
                  id    (second match)
                  idx   (.indexOf text token offset)]
              (when (< offset idx)
                (.appendChild fragment (.createTextNode doc (subs text offset idx))))
              (.appendChild fragment (if (contains? placeholder-ids id)
                                       (placeholder-span doc id)
                                       (.createTextNode doc token)))
              (recur (rest remaining) (+ idx (count token))))
            (when (< offset (count text))
              (.appendChild fragment (.createTextNode doc (subs text offset))))))
        (some-> (.-parentNode node)
                (.replaceChild fragment node))))))

(defn- normalize-template-placeholders [html placeholder-ids]
  (if (and (seq placeholder-ids) (not (str/blank? html)))
    (let [doc (.parseFromString (js/DOMParser.) html "text/html")]
      (letfn [(walk! [node]
                (doseq [child (vec (array-seq (.-childNodes node)))]
                  (cond
                    (= (.-nodeType child) js/Node.TEXT_NODE)
                    (replace-placeholder-text-node! doc placeholder-ids child)

                    (and (= (.-nodeType child) js/Node.ELEMENT_NODE)
                         (not= "template-placeholder" (.getAttribute child "data-type")))
                    (walk! child))))]
        (walk! (.-body doc))
        (.. doc -body -innerHTML)))
    html))

(def custom-blockquote-style
  "pdfkromi insists on rendering all borders hence the zero widths."
  (str "font-style: italic; padding-left: 1rem; border-color: #4f0891; quotes: none; border-style: solid; "
       "border-left-width: 2px; border-top-width:0; border-bottom-width:0; border-right-width:0; "
       "margin: 0 0 0.5rem 1rem;"))

(defn toggle-font-size
  "Toggle a paragraph node with given font size."
  [font-size *editor]
  (let [e         ^js @*editor
        is-active (.isActive e "paragraph" #js {:fontSize font-size})
        attr      (if is-active #js {:fontSize nil} #js {:fontSize font-size})]
    (-> e
        .chain
        .focus
        (.setNode "paragraph" attr)
        .run)))

(declare nodes)
(declare marks)

(rf/reg-sub ::data
  (fn [db [_ id]]
    (get-in db [::data id])))

(defn data-for-id-signal [[_ id _]]
  (rf/subscribe [::data id]))

(rf/reg-sub ::active-node
  data-for-id-signal
  :-> :active-node)

(rf/reg-sub ::active-marks
  data-for-id-signal
  :-> :active-marks)

(rf/reg-sub ::indent-state
  data-for-id-signal
  :-> :indent-state)

(rf/reg-event-db ::set-active-node
  (fn [db [_ id editor]]
    (assoc-in db [::data id :active-node]
              (or (some-> (p/find-first nodes
                                        (fn [[_ {:keys [is-active?]}]]
                                          (when is-active? (is-active? editor))))
                          first)
                  "unstyled"))))

(rf/reg-event-db ::set-active-marks
  (fn [db [_ id editor]]
    (assoc-in db [::data id :active-marks]
              (->> marks
                   (filter (fn [[_ {:keys [is-active?]}]]
                             (when is-active? (is-active? editor))))
                   (map first)
                   set))))

(rf/reg-event-db ::set-indent-state
  (fn [db [_ id ^js editor]]
    (assoc-in db [::data id :indent-state]
              {:sink (-> editor .can (.sinkListItem "listItem"))
               :lift (-> editor .can (.liftListItem "listItem"))})))

(rf/reg-event-fx ::update-editor-state
  (fn [_ [_ id editor]]
    {:fx [[:dispatch [::set-active-node id editor]]
          [:dispatch [::set-active-marks id editor]]
          [:dispatch [::set-indent-state id editor]]]}))

(rf/reg-event-db ::clear-editor-state
  (fn [db [_ id]]
    (p/dissoc-in db [::data id])))

(defn is-active?
  "Query the editor whether given node/mark is active at the current position with the given attributes."
  ([name attrs editor]
   (when editor
     (.isActive editor name attrs)))
  ([name editor]
   (is-active? name #js {} editor)))

(defn toggle-extension
  "Run given toggle function on the editor."
  [toggle-fn *editor]
  (let [^js e @*editor]
    (-> e
        .chain
        .focus
        toggle-fn
        ^js (.run))))

(defn toggle-custom-node
  "Toggle a custom node with given fallback and optional attributes."
  [custom-node fallback attrs *editor]
  (let [^js e @*editor]
    (-> e
        .chain
        .focus
        (.toggleNode custom-node fallback attrs)
        ^js (.run))))

(defn clear-nodes
  "Set a paragraph without any attributes."
  [*editor]
  (let [e ^js @*editor]
    (-> e
        .chain
        .focus
        (.setNode "paragraph")
        .run)))

(defn sink-list-item
  "Indent the node at the current position."
  [*editor]
  (let [e ^js @*editor]
    (-> e
        .chain
        .focus
        (.sinkListItem "listItem")
        .run)))

(defn lift-list-item
  "De-indent the node at the current position."
  [*editor]
  (let [e ^js @*editor]
    (-> e
        .chain
        .focus
        (.liftListItem "listItem")
        .run)))

(defn get-content
  "Return the editor content as HTML. Returns an empty string instead of `<p></p>`
   when the editor is empty, unless `dont-coerce-to-empty-string?` is true."
  [^js editor dont-coerce-to-empty-string?]
  (if (and (not dont-coerce-to-empty-string?) (.-isEmpty editor))
    ""
    (.getHTML editor)))

(defn insert-fragment
  "Insert a content fragment at the current position."
  [editor on-change dont-coerce-to-empty-string? content]
  (-> ^js editor
      .chain
      ;; At least Tiptap 2.8.0 will insert empty rows in lists unless this is set.
      (.insertContent content (clj->js {:parseOptions {:preserveWhitespace false}}))
      .run)
  (when on-change
    (-> ^js editor
        (get-content dont-coerce-to-empty-string?)
        on-change)))

(def nodes
  "Toggle and is-active? functions for nodes (block elements)."
  {"header-one"          {:toggle     (partial toggle-extension #(.toggleHeading ^js % #js {:level 1}))
                          :is-active? (partial is-active? "heading" #js {:level 1})}
   "header-two"          {:toggle     (partial toggle-extension #(.toggleHeading ^js % #js {:level 2}))
                          :is-active? (partial is-active? "heading" #js {:level 2})}
   "header-three"        {:toggle     (partial toggle-extension #(.toggleHeading ^js % #js {:level 3}))
                          :is-active? (partial is-active? "heading" #js {:level 3})}
   "unstyled"            {:toggle clear-nodes}
   "small"               {:toggle     (partial toggle-font-size "0.8em")
                          :is-active? (partial is-active? "paragraph" #js {:fontSize "0.8em"})}
   "large"               {:toggle     (partial toggle-font-size "1.2em")
                          :is-active? (partial is-active? "paragraph" #js {:fontSize "1.2em"})}
   "blockquote"          {:toggle     (partial toggle-extension #(.toggleBlockquote ^js %))
                          :is-active? (partial is-active? "blockquote")}
   "unordered-list-item" {:toggle     (partial toggle-extension #(.toggleBulletList ^js %))
                          :is-active? (partial is-active? "bulletList")}
   "ordered-list-item"   {:toggle     (partial toggle-extension #(.toggleOrderedList ^js %))
                          :is-active? (partial is-active? "orderedList")}
   "no-spacing"          {:toggle     (partial toggle-custom-node "noSpacing" "paragraph" #js {:style "margin: 0;"})
                          :is-active? (partial is-active? "noSpacing")}})

(def marks
  "Toggle and is-active? functions for marks."
  {"bold"        {:toggle     (partial toggle-extension #(.toggleBold ^js %))
                  :is-active? (partial is-active? "bold")}
   "italic"      {:toggle     (partial toggle-extension #(.toggleItalic ^js %))
                  :is-active? (partial is-active? "italic")}
   "underline"   {:toggle     (partial toggle-extension #(.toggleUnderline ^js %))
                  :is-active? (partial is-active? "underline")}
   "superscript" {:toggle     (partial toggle-extension #(.toggleSuperscript ^js %))
                  :is-active? (partial is-active? "superscript")}})

(defn button [{:keys [class toggle-fn icon name *editor tr on-change dont-coerce-to-empty-string? disabled]}]
  [:button.border-0.fill-current.bg-transparent.hover:bg-ui-violet-100.hover:text-white.outline-none.rounded-none
   {:class         class
    :on-click      (fn [e] (.preventDefault e))
    :on-mouse-down (fn [e]
                     (.preventDefault e)
                     (toggle-fn *editor)
                     (when on-change
                       (-> ^js @*editor
                           (get-content dont-coerce-to-empty-string?)
                           on-change)))
    :disabled      disabled
    :aria-label    (tr [(keyword "text" (str/lower-case name))])}
   [:> icon {}]])

(defn toggle [{:keys [name tr active-marks style *editor] :as props}]
  [button
   (-> props
       (merge {:tr         tr
               :*editor    *editor
               :toggle-fn  (-> marks (get name) :toggle)
               :aria-label (tr [(keyword "text" (str/lower-case name))])})
       (p/assoc-if :class (str style " " (when (get active-marks name) "bg-ui-gray text-black"))))])


(defn editor-tools [{:keys [*editor on-change on-blur style tr phrases authority-group-id default-phrase-category id
                            dont-coerce-to-empty-string?]}]
  (let [active-node  (<sub [::active-node id])
        toggle-state (util/mmap {:active-marks (<sub [::active-marks id])}
                                tr style *editor dont-coerce-to-empty-string?)
        indent-state (<sub [::indent-state id])
        on-change-fn (fn [new-val]
                       (when on-blur (on-blur new-val))
                       (when on-change (on-change new-val)))]
    [:div.flex.border-b.border-solid.border-gray-300.flex-wrap
     [:div.flex
      [ui-select/select
       {:id           "block-style"
        :inline?      true
        :value        active-node
        :allow-empty? false
        :class        "border-r border-solid border-gray-300"
        :label-text   (tr [:ui-rt/block-style])
        :hide-label   true
        :values       ["header-one"
                       "header-two"
                       "header-three"
                       "unstyled"
                       "small"
                       "large"
                       "blockquote"
                       "unordered-list-item"
                       "ordered-list-item"
                       "no-spacing"]
        :val->str     (comp tr vector (partial keyword "text"))
        :on-change    (fn [node-name]
                        ((-> node-name nodes :toggle) *editor))}]
      [:div.flex
       [:<>
        [button (assoc toggle-state
                       :disabled (-> indent-state :lift not)
                       :name "deindent"
                       :icon TextIndentLess24
                       :toggle-fn lift-list-item)]
        [button (assoc toggle-state
                       :disabled (-> indent-state :sink not)
                       :name "indent"
                       :icon TextIndentMore24
                       :toggle-fn sink-list-item)]
        [:span " "]]

       [toggle (assoc toggle-state :name "bold" :icon TextBold24)]
       [toggle (assoc toggle-state :name "italic" :icon TextItalic24)]
       [toggle (assoc toggle-state :name "underline" :icon TextUnderline24)]
       [toggle (assoc toggle-state :name "superscript" :icon TextSuperscript24)]]]
     [:div.flex-1] ; spacer
     (if (<sub [::group-subs/has-feature? authority-group-id :group.feature/model-codes-integration])
       [cc/button
        {:kind     :ghost
         :on-click #(>evt [::phrase-code-modal/open-phrase-code-modal id authority-group-id default-phrase-category])}
        (tr [:phrase/add-phrase-or-code])]
       [ui/phrase-selector
        {:tr               tr
         :default-category default-phrase-category
         :phrases          phrases
         :editing?         true
         :id               id
         :on-change        (fn [text]
                             (insert-fragment @*editor on-change-fn dont-coerce-to-empty-string? text))}])]))

(defn submit-content
  "Deliver editor content as HTML to given callback."
  [^js editor callback dont-coerce-to-empty-string?]
  (when callback
    (-> editor
        (get-content dont-coerce-to-empty-string?)
        callback)))

(def base-extensions
  [Document
   CustomParagraph
   Text
   Heading
   ListItem
   BulletList
   OrderedList
   (.configure Blockquote
               (clj->js {:HTMLAttributes
                         {:style custom-blockquote-style}}))
   TextStyle
   Bold
   Underline
   Italic
   Superscript
   Link
   NoSpacing
   ;; ProseMirror owns the DOM, so the browser's native undo does nothing. Without this
   ;; extension Mod-Z is a no-op in the editor.
   UndoRedo])

(defn editor-extensions [placeholder placeholder-ids]
  (cond-> base-extensions
    (seq placeholder-ids) (conj
                            ;; template placeholders are highlights to %placeholders%, for replacement targets
                            (.configure TemplatePlaceholder
                                        (clj->js {:placeholderIds (set placeholder-ids)})))
    ;; placeholder means the gray text rendered on empty editors
    placeholder (conj (.configure Placeholder (clj->js {:placeholder placeholder})))))

(def tiptap-classes
  (str "tiptap-editor prose prose-sm max-w-none break-words focus:outline-none prose-a:text-ui-blue prose-h1:text-xl "
       "prose-h1:font-bold prose-h2:text-lg prose-h3:text-base prose-h1:mt-4 prose-h1:mb-2 prose-h2:mt-4 prose-h2:mb-2 "
       "prose-h3:mt-4 prose-h3:mb-2 prose-ul:my-2 prose-ol:my-2"))

(defn create-editor [{:keys [id *container-ref placeholder template-placeholders dont-coerce-to-empty-string?
                             initial-value on-change on-blur on-create]}]
  (let [editable?       (boolean (or on-change on-blur))
        placeholder-ids (set (map :id template-placeholders))]
    (tiptap/Editor.
      (clj->js
        (cond-> {:element     @*container-ref
                 :editorProps {:attributes
                               {:class      tiptap-classes
                                :spellcheck true}}
                 :extensions  (editor-extensions placeholder placeholder-ids)
                 :content     (normalize-template-placeholders initial-value placeholder-ids)
                 ;; Here it is important for editable? to be a boolean, nil will leave the text editable.
                 :editable    editable?}
          on-create (assoc :onCreate on-create)
          editable? (merge {:onUpdate          (fn [^js event]
                                                 (let [^js e (.-editor event)]
                                                   (>evt [::update-editor-state id e])
                                                   (normalize-template-placeholder-editor! e placeholder-ids)
                                                   (submit-content e on-change dont-coerce-to-empty-string?)))
                            :onBlur            (fn [^js event]
                                                 (let [^js e (.-editor event)]
                                                   (normalize-template-placeholder-editor! e placeholder-ids)
                                                   (submit-content e on-blur dont-coerce-to-empty-string?)))
                            :onSelectionUpdate (fn [^js event] (>evt [::update-editor-state id (.-editor event)]))
                            :onTransaction     (fn [^js event] (>evt [::update-editor-state id (.-editor event)]))}))))))

(defn content-overflows? [*container max-height]
  (> (.-scrollHeight @*container) max-height))

(defn set-max-height! [*container max-height]
  (when max-height
    (set! (.. @*container -style -maxHeight) max-height)))

(defn set-overflow! [*container value]
  (set! (.. @*container -style -overflow) value))

(defn component-editor-editable? [argv]
  (some some? (map argv [:on-change :on-blur])))

(defn- current-editor-callbacks
  "Forward events to current props without rebuilding the editor."
  [component]
  (when (component-editor-editable? (r/props component))
    {:on-change (fn [content]
                  (some-> component r/props :on-change (apply [content])))
     :on-blur   (fn [content]
                  (some-> component r/props :on-blur (apply [content])))}))

(defn- editor-extension-props [props]
  (select-keys props [:placeholder :template-placeholders]))

(defn- guard-resize-click!
  "Guard a native resize gesture started on container by pointer-id. A release outside the editor can
  target an ancestor with its click, bypassing the editor's handler and collapsing the containing section.
  Temporary document capture listeners suppress that outside click after the matching pointer is released.
  Clicks inside the editor retain their normal handling.

  The listeners are removed on cancellation, after the release click, or on the next timer tick following
  release if no click occurs, so later independent clicks are unaffected. Returns an idempotent cleanup
  function for the caller to invoke when replacing the gesture, changing editor mode, or unmounting."
  [container pointer-id]
  (let [document          (.-ownerDocument container)
        *released?        (atom false)
        *timeout          (atom nil)
        *listener-keys    (atom [])
        cleanup!          (fn []
                            (run! googe/unlistenByKey @*listener-keys)
                            (reset! *listener-keys [])
                            (when-let [timeout @*timeout]
                              (timer/clear timeout)))
        on-click          (fn [e]
                            (when @*released?
                              (cleanup!)
                              ;; A resize release can target an ancestor, bypassing the editor's click handler.
                              (when-not (dom/contains container (.-target e))
                                (.preventDefault e)
                                (.stopPropagation e))))
        on-pointer-up     (fn [e]
                            (when (= pointer-id (.-pointerId e))
                              (reset! *released? true)
                              ;; Keep the guard through the release click, but never consume a later independent click.
                              (reset! *timeout (timer/callOnce cleanup! 0))))
        on-pointer-cancel (fn [e]
                            (when (= pointer-id (.-pointerId e))
                              (cleanup!)))]
    (reset! *listener-keys
            [(googe/listen document "click" on-click true)
             (googe/listen document "pointerup" on-pointer-up true)
             (googe/listen document "pointercancel" on-pointer-cancel true)])
    cleanup!))

(defn editor-component
  "max-height defines in pixels the maximum content height visible in read-only mode. A show more/less toggle
  appears when needed. When scroll? is true, the content is scrollable within the max-height instead.
  disable-read-more? displays all read-only content without the toggle.
  Edit mode includes a native vertical resize handle."
  [{:keys [id tr *editor initial-value on-change on-blur max-height placeholder template-placeholders
           dont-coerce-to-empty-string? scroll? disable-read-more?]}]
  (let [effective-max-height   (or max-height 150)
        *expanded?             (r/atom false)
        *container-ref         (r/atom nil)
        *overflows?            (r/atom false)
        *resize-cleanup        (atom nil)
        apply-read-only-style! (fn []
                                 (js/requestAnimationFrame
                                   (fn []
                                     (when @*container-ref
                                       (cond
                                         disable-read-more?
                                         (do
                                           (set-max-height! *container-ref "")
                                           (set-overflow! *container-ref "visible"))

                                         scroll?
                                         (do
                                           (set-max-height! *container-ref (str effective-max-height "px"))
                                           (set-overflow! *container-ref "auto"))

                                         ;; Temporarily remove max-height to measure true scrollHeight
                                         :else
                                         (do
                                           (set-max-height! *container-ref "")
                                           (let [overflows? (content-overflows? *container-ref effective-max-height)]
                                             (set-max-height! *container-ref (str effective-max-height "px"))
                                             (set-overflow! *container-ref "hidden")
                                             (reset! *overflows? overflows?))))
                                       ;; Reset after the height changes, which can otherwise restore the old offset.
                                       (set! (.-scrollTop @*container-ref) 0)))))]
    (r/create-class
      {:component-did-mount
       (fn [this]
         (reset! *editor (create-editor (-> (util/mmap
                                              id *container-ref placeholder dont-coerce-to-empty-string?
                                              initial-value template-placeholders)
                                            (merge (current-editor-callbacks this))
                                            (p/assoc-if :on-create (when-not (or on-change on-blur)
                                                                     apply-read-only-style!))))))

       :component-did-update
       (fn [this [_ old-argv]] ;; Need to react to changes that require rebuilding editor extensions.
         (let [[_ new-props & _] (r/argv this)
               was-editable?     (component-editor-editable? old-argv)
               is-editable?      (component-editor-editable? new-props)
               mode-changed?     (not= was-editable? is-editable?)
               options-changed?  (not= (editor-extension-props old-argv)
                                       (editor-extension-props new-props))
               initial-value     (if mode-changed?
                                   (:initial-value new-props)
                                   (get-content @*editor dont-coerce-to-empty-string?))]
           (when (or mode-changed? options-changed?)
             (when-let [cleanup @*resize-cleanup]
               (cleanup))
             (.destroy ^js @*editor)
             (reset! *expanded? false)
             (reset! *editor (create-editor (merge (util/mmap
                                                     id *container-ref dont-coerce-to-empty-string?)
                                                   (current-editor-callbacks this)
                                                   {:placeholder           (:placeholder new-props)
                                                    :template-placeholders (:template-placeholders new-props)
                                                    :initial-value         initial-value
                                                    :on-create             (when-not is-editable? apply-read-only-style!)}))))))

       :component-will-unmount
       (fn [_]
         (when-let [cleanup @*resize-cleanup]
           (cleanup))
         (when-let [e ^js @*editor]
           (.destroy e)
           (reset! *editor nil)
           (>evt [::clear-editor-state id])))

       :reagent-render
       (fn [{:keys [on-change on-blur]}]
         (let [read-only? (and (nil? on-change) (nil? on-blur))]
           [:div.min-w-0.max-w-full
            [:div.editor-container
             {:ref                     #(reset! *container-ref %)
              :on-pointer-down-capture (when-not read-only?
                                         (fn [e]
                                           (let [container (.-currentTarget e)]
                                             (when (and (= 0 (.-button e))
                                                        (= container (.-target e)))
                                               (when-let [cleanup @*resize-cleanup]
                                                 (cleanup))
                                               (reset! *resize-cleanup
                                                       (guard-resize-click! container (.-pointerId e)))
                                               (.setPointerCapture container (.-pointerId e))))))
              :on-click                (when-not read-only?
                                         (fn [e]
                                           (when (= (.-currentTarget e) (.-target e))
                                             (.stopPropagation e)
                                             (some-> ^js @*editor .-commands .focus))))
              :style                   (merge {:max-width     "100%"
                                               :overflow-wrap "anywhere"
                                               :transition    "max-height 0.3s ease-out"}
                                              (when-not read-only?
                                                ;; Override read-only limits when the same container enters edit mode.
                                                {:resize     "vertical"
                                                 :overflow   "auto"
                                                 :height     (str effective-max-height "px")
                                                 :min-height "5rem"
                                                 :max-height "none"}))}]
            (when (and (not disable-read-more?) (not scroll?) read-only? @*overflows?)
              [ui/expand-button {:button-props    {:size :small}
                                 :tr              tr
                                 :expanded?       @*expanded?
                                 :expand-handler  #(if @*expanded?
                                                     (do
                                                       (set-max-height! *container-ref (str effective-max-height "px"))
                                                       (set-overflow! *container-ref "hidden")
                                                       (reset! *expanded? false))
                                                     (do
                                                       (set-max-height! *container-ref "")
                                                       (set-overflow! *container-ref "visible")
                                                       (reset! *expanded? true)))
                                 :title-expanded  (tr [:show-less])
                                 :title-collapsed (tr [:show-more])}])]))})))

(defn editor-impl
  [{:keys [id editor initial-value initial-html-value on-change on-blur code-reference-fn default-phrase-category
           button-style tr phrases authority-group-id max-height placeholder template-placeholders
           dont-coerce-to-empty-string? scroll? disable-read-more?]}]
  (r/with-let [*editor (r/atom nil)]
    (let [read-only? (and (nil? on-change) (nil? on-blur))]
      [:div.rich-text-editor.min-w-0.max-w-full
       (merge {:data-cy id}
              (when-not read-only?
                {:class "bg-gray-200 border rounded border-solid focus-within:border-ui-blue rich-text-editor--editing"}))
       (when-not read-only?
         [editor-tools (util/mmap {:style button-style}
                                  id
                                  *editor
                                  on-change
                                  on-blur
                                  code-reference-fn
                                  default-phrase-category
                                  tr
                                  phrases
                                  authority-group-id
                                  dont-coerce-to-empty-string?)])
       [:div.leading-snug
        (when-not read-only?
          {:on-click #(some-> ^js @*editor
                              .-commands
                              .focus)
           :class    "p-2 bg-white min-h-20 cursor-text rounded"})

        [editor-component (util/mmap {:initial-value (or initial-value initial-html-value)}
                                     id
                                     tr
                                     *editor
                                     on-change
                                     on-blur
                                     max-height
                                     placeholder
                                     template-placeholders
                                     dont-coerce-to-empty-string?
                                     scroll?
                                     disable-read-more?)]]

       (when (and on-change
                  (<sub [::group-subs/has-feature? authority-group-id :group.feature/model-codes-integration]))
         [phrase-code-modal/modal tr id editor (partial insert-fragment @*editor) on-change code-reference-fn phrases default-phrase-category authority-group-id])])))
